import React, { useCallback, useEffect, useState } from "react";
import ProductCard from "@components/card/ProductCard";
import styled from "@emotion/styled";
import ProductListFilter from "@pages/myPage/_component/ProductListFilter";

import useIntersection from "@hook/useIntersection";
import useAxios from "@libs/axios/useAxios";
import { LOAD_SIZE_10 } from "@libs/reactQuery/queryClient";
import { GET_PRODUCT_URL } from "@libs/apiUrl";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { useApiRequest } from "@utils/ApiRequest";

const Container = styled.div({
  display: "flex",
  flexDirection: "column",
});

const CardContainer = styled.div(
  () =>
    ({
      width: "100%",
      display: "grid",
      gridTemplateColumns: "repeat(2, 1fr)",
    }) as const,
);

type Props = {
  haveFilter?: boolean;
  sellerId?: number;
  onTotalCount?: (number: number) => void;
  saved?: boolean;
};
const ShopListPanel = ({
  haveFilter,
  sellerId,
  onTotalCount,
  saved = false,
}: Props) => {
  const { axiosInstance } = useAxios();
  const lang = window.localStorage.getItem("language");
  const params = useParams();
  const [sort, setSort] = useState("DATE_DESC");

  const { getProductSort } = useApiRequest();
  const { data: productSort } = useQuery({
    queryKey: ["getProductList"],
    queryFn: () => getProductSort(),
    enabled: !!haveFilter,
  });

  const getProductList = useCallback(
    async ({ pageParam = 1 }) => {
      const url =
        `${GET_PRODUCT_URL}?page=${pageParam}&limit=${LOAD_SIZE_10}` +
        (sellerId && !saved ? `&seller_id=${sellerId}` : "") +
        (params?.brandId ? `&brand_id=${params.brandId}` : "") +
        (haveFilter && sort ? `&order_by=${sort}` : "") +
        (saved ? `&is_liked=1` : "");

      const res = await axiosInstance.get(url);
      if (res.status === 200) {
        return res.data;
      }
    },
    [axiosInstance, sellerId, params.brandId, sort, haveFilter, saved],
  );

  const {
    data: productList,
    fetchNextPage,
    isLoading,
  } = useInfiniteQuery({
    queryKey: ["productList", sellerId, params?.brandId, sort],
    queryFn: ({ pageParam = 1 }) => getProductList({ pageParam }),
    initialPageParam: 1,
    getNextPageParam: (lastPages, pages) =>
      pages?.length < lastPages.data.total_count / LOAD_SIZE_10
        ? pages.length + 1
        : undefined,
  });

  const { setObserverTarget } = useIntersection(() => {
    fetchNextPage();
  });

  useEffect(() => {
    if (productList?.pages[0]?.data?.total_count) {
      onTotalCount?.(productList?.pages[0]?.data.total_count);
      if (window.location.pathname.includes("my-page")) {
        sessionStorage.setItem(
          "productAmount",
          productList?.pages[0]?.data.total_count || "0",
        );
      } else {
        sessionStorage.setItem("productAmount", "0");
      }
    }
  }, [onTotalCount, productList]);

  return (
    <Container>
      {haveFilter && (
        <ProductListFilter
          totalCount={productList?.pages[0].data.total_count}
          filterList={productSort}
          onSortChange={setSort}
        />
      )}

      <CardContainer>
        {productList?.pages?.map((page) => {
          return page?.data?.list?.map((product: any) => {
            const brand =
              product[`brand_name_${lang}`] || product.brand_name_ko;
            const name =
              product[`product_name_${lang}`] || product.product_name_ko;
            return (
              <ProductCard
                key={product.product_id}
                id={product.product_id}
                name={name}
                brand={brand}
                src={product.thumbnail_url}
                price={product.price}
                likeCount={product.like_total}
                chatCount={product.qna_total}
                haveLike
                soldout={product.is_sold_out === 1}
                isLiked={product.is_liked === 1}
                condition={product.product_condition}
                isActive={product.is_active ? false : true}
              />
            );
          });
        })}
      </CardContainer>
      {!isLoading && <div ref={setObserverTarget} />}
    </Container>
  );
};

export default ShopListPanel;
